import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtInt, fmtPct } from "@/api";
import { useI18n } from "@/i18n";
import { X, MagnifyingGlass, CaretLeft, Buildings, MapPin, Train } from "@phosphor-icons/react";

/**
 * Level-1 drill-down popup for the Dashboard KPI tiles.
 * kind: "operators" | "cities" | "platforms"
 * Clicking an operator navigates to /operators/:name (level 2).
 * Clicking a city switches to an in-modal city view (operators of that city).
 */
export default function DrilldownModal({ kind, onClose }) {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [cityDetail, setCityDetail] = useState(null); // { city, rows }

  const L = (fr, en) => (lang === "fr" ? fr : en);

  useEffect(() => {
    setRows(null);
    api.get(`/drilldown/${kind}`).then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  }, [kind]);

  const openOperator = (name) => {
    onClose();
    navigate(`/operators/${encodeURIComponent(name)}`);
  };

  const openCity = (city) => {
    setCityDetail({ city, rows: null });
    api.get("/drilldown/operators", { params: { city } })
      .then((r) => setCityDetail({ city, rows: r.data.rows }))
      .catch(() => setCityDetail({ city, rows: [] }));
  };

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      JSON.stringify(r).toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const TITLES = {
    operators: L("Opérateurs", "Operators"),
    cities: L("Villes", "Cities"),
    platforms: L("Plateformes", "Platforms"),
  };
  const ICONS = { operators: Buildings, cities: MapPin, platforms: Train };
  const Icon = ICONS[kind];

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-3 md:p-8"
         style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
         onClick={onClose} data-testid="drilldown-backdrop">
      <div className="tile w-full max-w-4xl max-h-[85vh] flex flex-col fade-in overflow-hidden"
           onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "var(--shadow-lg)", padding: 0 }}
           data-testid={`drilldown-modal-${kind}`}>

        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4"
             style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2.5 min-w-0">
            {cityDetail ? (
              <button onClick={() => setCityDetail(null)}
                      className="flex items-center gap-1 text-[12px] font-mono hover:text-[var(--accent)] transition-colors shrink-0"
                      style={{ color: "var(--text-secondary)" }}
                      data-testid="drilldown-back">
                <CaretLeft size={13} weight="bold" /> {TITLES.cities}
              </button>
            ) : (
              <Icon size={18} weight="fill" style={{ color: "var(--accent)" }} />
            )}
            <h3 className="text-[16px] font-semibold tracking-tight truncate">
              {cityDetail ? `${cityDetail.city} — ${L("opérateurs", "operators")}` : TITLES[kind]}
              {!cityDetail && rows && <span className="font-mono text-[12px] ml-2" style={{ color: "var(--text-tertiary)" }}>({rows.length})</span>}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-[8px] hover:bg-[var(--bg-subtle)] transition-colors"
                  data-testid="drilldown-close" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        {!cityDetail && (
          <div className="px-5 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2"
                               style={{ color: "var(--text-tertiary)" }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={L("Rechercher…", "Search…")}
                className="input-tech w-full pl-9"
                data-testid="drilldown-search"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto overflow-x-auto">
          {cityDetail ? (
            <CityOperators detail={cityDetail} L={L} onOperator={openOperator} />
          ) : !filtered ? (
            <div className="py-16 text-center text-[12px] font-mono" style={{ color: "var(--text-tertiary)" }}>
              {L("Chargement…", "Loading…")}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-[13px]" style={{ color: "var(--text-tertiary)" }}>
              {L("Aucun résultat", "No results")}
            </div>
          ) : kind === "operators" ? (
            <OperatorsTable rows={filtered} L={L} onOperator={openOperator} />
          ) : kind === "cities" ? (
            <CitiesTable rows={filtered} L={L} onCity={openCity} />
          ) : (
            <PlatformsTable rows={filtered} L={L} />
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-2.5 text-[11px] font-mono" style={{ borderTop: "1px solid var(--border)", color: "var(--text-tertiary)" }}>
          {kind === "operators" && L("Cliquez sur un opérateur pour ouvrir sa fiche 360°", "Click an operator to open its 360° profile")}
          {kind === "cities" && L("Cliquez sur une ville pour voir ses opérateurs", "Click a city to see its operators")}
          {kind === "platforms" && L("Vue agrégée par plateforme de matériel roulant", "Aggregated view per rolling-stock platform")}
        </div>
      </div>
    </div>
  );
}

function OperatorsTable({ rows, L, onOperator }) {
  return (
    <table className="tech w-full" data-testid="drilldown-operators-table">
      <thead>
        <tr>
          <th>{L("Opérateur", "Operator")}</th>
          <th className="text-right">{L("Unités", "Units")}</th>
          <th className="text-right">W%</th>
          <th>{L("Villes", "Cities")}</th>
          <th className="text-right">{L("Opps", "Opps")}</th>
          <th className="text-right">{L("Contacts", "Contacts")}</th>
          <th className="text-right">{L("Visites", "Visits")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.operator} className="cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors"
              onClick={() => onOperator(r.operator)} data-testid={`drilldown-operator-${r.operator}`}>
            <td className="font-medium" style={{ color: "var(--accent)" }}>{r.operator}</td>
            <td className="num">{fmtInt(r.units)}</td>
            <td className="num">{fmtPct(r.share_pct)}</td>
            <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{r.cities.join(", ")}</td>
            <td className="num">{r.opportunities}</td>
            <td className="num">{r.contacts}</td>
            <td className="num">{r.visits}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CitiesTable({ rows, L, onCity }) {
  return (
    <table className="tech w-full" data-testid="drilldown-cities-table">
      <thead>
        <tr>
          <th>{L("Ville", "City")}</th>
          <th className="text-right">{L("Unités", "Units")}</th>
          <th className="text-right">W%</th>
          <th className="text-right">{L("Plateformes", "Platforms")}</th>
          <th>{L("Opérateurs", "Operators")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.city} className="cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors"
              onClick={() => onCity(r.city)} data-testid={`drilldown-city-${r.city}`}>
            <td className="font-medium" style={{ color: "var(--accent)" }}>{r.city}</td>
            <td className="num">{fmtInt(r.units)}</td>
            <td className="num">{fmtPct(r.share_pct)}</td>
            <td className="num">{r.platforms_count}</td>
            <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{r.operators.join(", ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PlatformsTable({ rows, L }) {
  return (
    <table className="tech w-full" data-testid="drilldown-platforms-table">
      <thead>
        <tr>
          <th>{L("Plateforme", "Platform")}</th>
          <th>{L("Constructeurs", "Suppliers")}</th>
          <th>{L("Opérateurs", "Operators")}</th>
          <th className="text-right">{L("Rames", "Trainsets")}</th>
          <th className="text-right">{L("Unités", "Units")}</th>
          <th className="text-right">W%</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.platform} data-testid={`drilldown-platform-${r.platform}`}>
            <td className="font-medium">{r.platform}</td>
            <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{r.suppliers.join(", ") || "—"}</td>
            <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{r.operators.join(", ")}</td>
            <td className="num">{fmtInt(r.trainsets)}</td>
            <td className="num">{fmtInt(r.units)}</td>
            <td className="num">{fmtPct(r.share_pct)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CityOperators({ detail, L, onOperator }) {
  if (!detail.rows) {
    return (
      <div className="py-16 text-center text-[12px] font-mono" style={{ color: "var(--text-tertiary)" }}>
        {L("Chargement…", "Loading…")}
      </div>
    );
  }
  if (detail.rows.length === 0) {
    return (
      <div className="py-16 text-center text-[13px]" style={{ color: "var(--text-tertiary)" }}>
        {L("Aucun opérateur dans cette ville", "No operator in this city")}
      </div>
    );
  }
  return <OperatorsTable rows={detail.rows} L={L} onOperator={onOperator} />;
}
